import { getGlobalFontManager } from '../../FontManager';

/**
 * A template font resolved for injection into a composition: the template `fonts` index it occupies
 * (so it can be bound to `--l5-font-<index>`), the family name that variable resolves to, and its
 * bytes as a data URI. All come from the global font manager, which owns the fetch-once cache.
 */
export interface HtmlFontFace {
    index: number;
    familyName: string;
    dataUri: string;
}

/** A template-font slot the composition references: its `fonts` index and URL. */
export interface HtmlFontSlot {
    index: number;
    url: string;
}

// A hung fetch must not stall rasterization forever; unresolved fonts are skipped for the frame
// and picked up once they land.
const FETCH_TIMEOUT_MS = 8000;

/**
 * Fetch the given font slots (via the font manager's shared cache) and return the faces resolved by
 * the time every fetch settles or the timeout elapses. Failures and stragglers are skipped, never
 * thrown — a missing font must degrade to a fallback render, not block the frame.
 */
export async function resolveHtmlFontFaces(slots: HtmlFontSlot[]): Promise<HtmlFontFace[]> {
    const fontManager = getGlobalFontManager();
    const timeout = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), FETCH_TIMEOUT_MS);
    });
    await Promise.race([
        Promise.all(slots.map(async (slot) => fontManager.getFontDataUri(slot.url))),
        timeout,
    ]);
    return getResolvedHtmlFontFaces(slots);
}

/** The already-resolved faces for the given slots, unresolved ones skipped. Synchronous. */
export function getResolvedHtmlFontFaces(slots: HtmlFontSlot[]): HtmlFontFace[] {
    const fontManager = getGlobalFontManager();
    const faces: HtmlFontFace[] = [];
    for (const { index, url } of slots) {
        const dataUri = fontManager.getCachedFontDataUri(url);
        if (dataUri) {
            faces.push({ index, familyName: fontManager.familyNameForUrl(url), dataUri });
        }
    }
    return faces;
}
